// src/components/common/CourseInfo.jsx
import React from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const PERSON_PATH  = 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z';
const STAR_PATH    = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
const SCHOOL_PATH  = 'M12 3L1 9l11 6 9-4.91V17h2V9L12 3z';
const PRIVATE_PATH = 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm8 4v6m3-3h-6';

// ─── Sub-components ───────────────────────────────────────────────────────────

const BadgeIcon = ({ gradient, path, isPolyline }) => (
  <div className={`w-5 h-5 sm:w-6 sm:h-6 ${gradient} rounded-full flex items-center justify-center flex-shrink-0 shadow-sm`}>
    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" viewBox="0 0 24 24"
      fill={isPolyline ? 'none' : 'currentColor'}
      stroke={isPolyline ? 'currentColor' : 'none'}
      strokeWidth={isPolyline ? 2 : 0}
      strokeLinecap="round" strokeLinejoin="round"
    >
      <path d={path} />
    </svg>
  </div>
);

const Badge = ({ gradient, path, isPolyline, label, textColor, bgColor, borderColor }) => (
  <div className={`inline-flex items-center gap-1.5 ${bgColor} ${borderColor} border rounded-lg px-2.5 sm:px-3 py-1 sm:py-1.5 shadow-sm`}>
    <BadgeIcon gradient={gradient} path={path} isPolyline={isPolyline} />
    <span className={`text-xs sm:text-sm font-semibold ${textColor} whitespace-nowrap`}>
      {label}
    </span>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CourseInfo = ({ course }) => {
  const teacherName  = course?.teacher?.name || 'מורה';
  const isGroup      = ['group', 'קבוצתי'].includes(String(course?.type ?? '').toLowerCase().trim());
  const typeLabel    = isGroup ? 'קורס קבוצתי' : 'קורס פרטני';
  const typePath     = isGroup ? SCHOOL_PATH : PRIVATE_PATH;
  const typePolyline = !isGroup;

  return (
    <section className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 py-3" dir="rtl">
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl">

        {/* Background — matches OnlineClassHeader */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50" />

        {/* Card — matches OnlineClassHeader */}
        <div className="relative bg-white/80 backdrop-blur-xl border border-white/60 rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-6 shadow-2xl shadow-indigo-100/50">
          <div className="flex flex-col gap-3 sm:gap-4">

            {/* Title */}
            <div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-800 bg-clip-text text-transparent leading-snug">
                {course?.title ?? 'קורס ללא שם'}
              </h2>
              <div className="mt-1.5 h-0.5 w-12 sm:w-16 bg-gradient-to-r from-indigo-500 to-purple-400 rounded-full" />
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <Badge
                gradient="bg-gradient-to-br from-indigo-500 to-purple-500"
                path={PERSON_PATH}
                label={teacherName}
                textColor="text-indigo-700"
                bgColor="bg-indigo-50"
                borderColor="border-indigo-100"
              />
              {course?.subject && (
                <Badge
                  gradient="bg-gradient-to-br from-blue-500 to-cyan-500"
                  path={STAR_PATH}
                  label={course.subject}
                  textColor="text-blue-700"
                  bgColor="bg-blue-50"
                  borderColor="border-blue-100"
                />
              )}
              <Badge
                gradient="bg-gradient-to-br from-emerald-500 to-teal-500"
                path={typePath}
                isPolyline={typePolyline}
                label={typeLabel}
                textColor="text-emerald-700"
                bgColor="bg-emerald-50"
                borderColor="border-emerald-100"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseInfo;